import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, inject, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ColumnsModel, ColumnPreference } from '@models/ColumnsModel';
import { SearchProfileService } from '@services/search-profile.service';
import { ToastrService } from 'ngx-toastr';
import { CommonService } from '@services/common.service';
import { AppConstants } from '@constants/app.constant';

import { MatChipsModule } from '@angular/material/chips';
import { CommonModule } from '@angular/common';
/**
 * Popup component for selecting and reordering columns for profile tables.
 * Allows users to customize which columns are displayed and their order.
 */
@Component({
  selector: 'app-column-selection-popup',
  templateUrl: './column-selection-popup.component.html',
  styleUrl: './column-selection-popup.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    MatChipsModule,
  ],
})
export class ColumnSelectionPopupComponent implements OnInit {
  /** Active modal instance for closing the popup */
  private activeModal = inject(NgbActiveModal);
  /** Search profile service for column preferences */
  private spService = inject(SearchProfileService);
  /** Toastr service for notifications */
  private toastr = inject(ToastrService);
  /** Common utility service */
  private commonService = inject(CommonService);
  /** Application constants */
  constants = AppConstants;

  /** All available column items */
  @Input() allItems: ColumnsModel[] = [];
  /** Data source type ("golden profile" or "cross reference") */
  @Input() dataSource = '';
  /** Column preferences (not used directly in logic) */
  @Input() colPref = [];
  /** Currently selected columns */
  selectedItemsArray: ColumnsModel[] = [];
  /** Filtered items by data source */
  ItemsBySource: ColumnsModel[] = [];

  ngOnInit(): void {
    this.getSelectedItems();
  }

  /**
   * Initializes the selected columns based on user preferences or defaults.
   */
  getSelectedItems(): void {
    this.selectedItemsArray = [];
    if (this.dataSource === 'cross reference') {
      this.ItemsBySource = this.allItems.filter(
        (m) =>
          m.type === this.dataSource &&
          !this.spService.crMandatoryCols.includes(m.db_column_name)
      );
    } else {
      this.ItemsBySource = this.allItems.filter(
        (m) =>
          m.type === this.dataSource &&
          !this.spService.gpMandatoryCols.includes(m.db_column_name)
      );
    }

    let selectedItems = '';
    if (this.dataSource === 'golden profile') {
      selectedItems = this.spService.gpColPref();
    }
    if (this.dataSource === 'cross reference') {
      selectedItems = this.spService.crColPref();
    }
    if (selectedItems) {
      const itemsArray = Array.from(selectedItems.split(','));
      for (const item of itemsArray) {
        const modelItem = this.ItemsBySource.find(
          (m) => m.db_column_name === item
        );
        if (modelItem) {
          this.selectedItemsArray.push(modelItem);
        }
      }
    } else {
      for (const modelItem of this.ItemsBySource) {
        this.selectedItemsArray.push(modelItem);
      }
    }
  }

  /**
   * Adds a column to the selected list if not already present.
   */
  addItem(item: ColumnsModel, db_column_name: string): void {
    if (!this.selectedItemsArray.some((m) => m.db_column_name === db_column_name)) {
      this.selectedItemsArray.push(item);
    }
  }

  /**
   * Removes a column from the selected list by index.
   */
  removeItem(index: number): void {
    this.selectedItemsArray.splice(index, 1);
  }

  /**
   * Checks if a column is already added to the selected list.
   */
  isItemAdded(db_column_name: string): boolean {
    return this.selectedItemsArray.some(
      (m) => m.db_column_name === db_column_name
    );
  }

  /**
   * Handles drag-and-drop reordering of selected columns.
   */
  drop(event: CdkDragDrop<ColumnsModel[]>): void {
    moveItemInArray(
      this.selectedItemsArray,
      event.previousIndex,
      event.currentIndex
    );
  }

  /**
   * Saves the selected columns as user preferences and closes the modal.
   */
  saveColumns(): void {
    const columns = this.selectedItemsArray.map((m) => m.db_column_name);
    const comm_sep_cols = columns.join(',');
    const data: { golden_profile_columns: string; cross_reference_columns: string } = {
      golden_profile_columns: '',
      cross_reference_columns: '',
    };
    if (this.dataSource === 'golden profile') {
      data.golden_profile_columns = comm_sep_cols;
    }
    if (this.dataSource === 'cross reference') {
      data.cross_reference_columns = comm_sep_cols;
    }
    this.commonService.setShowLoader(true);
    this.spService.saveUserColumnPreference(data).subscribe({
      next: (res: ColumnPreference) => {
        this.toastr.success(res.message);
        if (this.dataSource === 'golden profile') {
          this.spService.gpColPref.set(comm_sep_cols);
        }
        if (this.dataSource === 'cross reference') {
          this.spService.crColPref.set(comm_sep_cols);
        }
        this.commonService.setShowLoader(false);
        this.activeModal.close();
      },
      error: () => {
        this.toastr.error('Some error occured');
        this.commonService.setShowLoader(false);
      },
    });
  }

  /**
   * Closes the modal without saving changes.
   */
  closeModal(): void {
    this.activeModal.close('closed');
  }
}
